import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { inr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { fmt12 } from "@/lib/time";
import { Calendar, CheckCircle, ArrowRight, Wallet, Sparkles, PlusCircle, Clock } from "lucide-react";

export default function CustomerOverview() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [wallet, setWallet] = useState(null);

  useEffect(() => {
    api.get("/bookings/mine").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/wallet/mine").then((r) => setWallet(r.data)).catch(() => {});
  }, []);

  const upcoming = bookings.filter((b) => ["pending", "confirmed"].includes(b.status));
  const past = bookings.filter((b) => b.status === "completed");
  const spent = past.reduce((s, b) => s + (b.amount || 0), 0);

  return (
    <div data-testid="customer-overview" className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="border-b-2 border-white/10 pb-6"
      >
        <div className="text-xs uppercase tracking-[0.25em] text-emerald-400 font-extrabold mb-1 flex items-center gap-2">
          <Sparkles size={16} className="text-amber-400" /> WELCOME VIP GUEST
        </div>
        <h1 className="text-4xl lg:text-5xl font-heading text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-emerald-400 font-bold">
          Hello, {user?.name?.split(" ")[0]}.
        </h1>
      </motion.div>

      {/* Stats & Wallet Grid */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="grid grid-cols-1 md:grid-cols-4 gap-6"
      >
        {/* Wallet Balance Card (Featured) */}
        <div className="rounded-2xl border border-emerald-400/80 p-6 bg-gradient-to-b from-emerald-950/30 via-black/90 to-emerald-950/20 shadow-xl shadow-emerald-950/20 flex flex-col justify-between space-y-3">
          <div className="flex justify-between items-center">
            <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-extrabold flex items-center gap-1.5">
              <Wallet size={14} /> Active Wallet Balance
            </div>
            <Link
              to="/membership"
              className="text-[10px] font-extrabold uppercase tracking-wider text-amber-300 hover:text-amber-200 flex items-center gap-1 bg-amber-950/50 border border-amber-400/40 px-2.5 py-1 rounded-lg transition-all"
            >
              <PlusCircle size={12} /> Top Up
            </Link>
          </div>
          <div className="font-heading text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 to-teal-200">
            {inr(wallet?.balance || 0)}
          </div>
          <div className="text-[11px] text-white/50 font-medium">
            {wallet?.membership_name ? (
              <span>Active Tier: <strong className="text-amber-300">{wallet.membership_name}</strong></span>
            ) : (
              <span>No active membership tier</span>
            )}
          </div>
        </div>

        {/* Upcoming Sessions */}
        <div className="rounded-2xl border border-amber-400/70 p-6 bg-gradient-to-b from-hpaper via-black/90 to-hpaper shadow-xl shadow-yellow-950/20 flex flex-col justify-between">
          <div className="text-[10px] uppercase tracking-widest text-amber-400 font-extrabold flex items-center gap-1.5">
            <Calendar size={14} /> Upcoming Sessions
          </div>
          <div className="font-heading text-4xl font-bold text-white mt-2">
            {upcoming.length}
          </div>
          <div className="text-[11px] text-white/40 font-medium">Active reservations</div>
        </div>

        {/* Completed Sessions */}
        <div className="rounded-2xl border border-white/15 p-6 bg-gradient-to-b from-hpaper via-black/90 to-hpaper shadow-xl flex flex-col justify-between">
          <div className="text-[10px] uppercase tracking-widest text-white/60 font-extrabold flex items-center gap-1.5">
            <CheckCircle size={14} className="text-emerald-400" /> Completed Visits
          </div>
          <div className="font-heading text-4xl font-bold text-white mt-2">
            {past.length}
          </div>
          <div className="text-[11px] text-white/40 font-medium">Fulfilled appointments</div>
        </div>

        {/* Lifetime Spend */}
        <div className="rounded-2xl border border-rose-400/70 p-6 bg-gradient-to-b from-hpaper via-black/90 to-hpaper shadow-xl shadow-rose-950/20 flex flex-col justify-between">
          <div className="text-[10px] uppercase tracking-widest text-rose-400 font-extrabold">
            Lifetime Spa Spend
          </div>
          <div className="font-heading text-3xl font-bold text-amber-300 mt-2">
            {inr(spent)}
          </div>
          <div className="text-[11px] text-white/40 font-medium">Total redeemed value</div>
        </div>
      </motion.div>

      {/* Upcoming Sessions Section */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9 }}
        className="space-y-4 pt-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-2xl text-white font-bold">Upcoming Sessions</h2>
          <Link to="/dashboard/appointments" className="text-amber-400 hover:text-amber-300 text-xs uppercase font-extrabold tracking-widest flex items-center gap-1">
            View All <ArrowRight size={14}/>
          </Link>
        </div>

        <div className="space-y-3">
          {upcoming.slice(0, 5).map((b) => (
            <div
              key={b.id}
              className="rounded-2xl border border-amber-400/60 p-5 bg-gradient-to-b from-hpaper via-black/90 to-hpaper shadow-lg flex flex-wrap gap-4 justify-between items-center hover:scale-[1.01] transition-all"
            >
              <div className="space-y-1">
                <div className="font-heading text-xl text-white font-bold flex items-center gap-2">
                  <Clock size={16} className="text-amber-400" />
                  <span>{b.date} · {fmt12(b.time)}</span>
                </div>
                <div className="text-xs text-white/70 flex items-center gap-2">
                  <span className="uppercase tracking-widest text-[10px] font-extrabold text-emerald-400">{b.booking_type}</span>
                  <span>•</span>
                  <span className="capitalize text-amber-300 font-semibold">{b.status}</span>
                </div>
              </div>

              <div className="font-heading text-xl font-bold text-amber-300">
                {inr(b.amount)}
              </div>
            </div>
          ))}

          {upcoming.length === 0 && (
            <div className="rounded-2xl border border-white/10 p-12 text-center text-white/50 bg-hpaper">
              No upcoming sessions reserved.{" "}
              <Link to="/book" className="text-amber-400 hover:underline font-bold">
                Book a session now →
              </Link>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
